import { css } from 'lit';
import * as t from '../styles/tokens.js';
import { hostReset, focusRing } from '../styles/mixins.js';

export default [
  hostReset,
  focusRing('.uwc-car__prev', '--uwc-color-primary', '#6366f1'),
  focusRing('.uwc-car__next', '--uwc-color-primary', '#6366f1'),
  focusRing('.uwc-car__dot',  '--uwc-color-primary', '#6366f1'),
  css`
    :host {
      display: block;
      position: relative;
      overflow: hidden;
    }

    /* ── Header / Footer slots ─────────────────────────────────────── */
    .uwc-car__header,
    .uwc-car__footer {
      padding: ${t.space2} 0;
    }
    .uwc-car__header:empty,
    .uwc-car__footer:empty { display: none; }

    /* ── Region — wraps viewport + nav buttons ─────────────────────── */
    .uwc-car__region {
      position: relative;
    }

    /* ── Vertical host stretches via flex so region fills user-set height */
    :host([orientation="vertical"]) {
      display: flex;
      flex-direction: column;
    }
    :host([orientation="vertical"]) .uwc-car__region {
      flex: 1;
      min-height: 0;
    }
    :host([orientation="vertical"]) .uwc-car__viewport {
      height: 100%;
    }

    /* ── Viewport — clips the track ────────────────────────────────── */
    .uwc-car__viewport {
      overflow: hidden;
      position: relative;
    }

    /* ── Track — flex row or column of item pages ──────────────────── */
    .uwc-car__track {
      display: flex;
      will-change: transform;
    }
    :host([orientation="horizontal"]) .uwc-car__track { flex-direction: row; }
    :host([orientation="vertical"])   .uwc-car__track { flex-direction: column; }

    /* ── Single item wrapper ────────────────────────────────────────── */
    .uwc-car__item {
      flex-shrink: 0;
      box-sizing: border-box;
      padding: 0 ${t.space2};
    }
    :host([orientation="vertical"]) .uwc-car__item {
      padding: ${t.space2} 0;
    }

    /* ── Navigation buttons ─────────────────────────────────────────── */
    .uwc-car__nav {
      display: flex;
      align-items: center;
      justify-content: space-between;
      position: absolute;
      top: 50%;
      left: 0;
      right: 0;
      transform: translateY(-50%);
      pointer-events: none;
      z-index: 2;
      padding: 0 ${t.space2};
    }
    :host([orientation="vertical"]) .uwc-car__nav {
      flex-direction: column;
      justify-content: space-between;
      top: 0;
      bottom: 0;
      left: 50%;
      right: auto;
      width: auto;
      transform: translateX(-50%);
      padding: ${t.space2} 0;
    }

    .uwc-car__prev,
    .uwc-car__next {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 2.25rem;
      height: 2.25rem;
      border-radius: ${t.radiusFull};
      border: 1px solid ${t.border};
      background: ${t.surface};
      color: ${t.text};
      cursor: pointer;
      pointer-events: all;
      transition:
        background ${t.durationBase},
        color ${t.durationBase},
        border-color ${t.durationBase},
        box-shadow ${t.durationBase};
      box-shadow: ${t.shadowSm};
      flex-shrink: 0;
    }
    .uwc-car__prev:hover,
    .uwc-car__next:hover {
      background: ${t.primary};
      color: ${t.colorWhite};
      border-color: ${t.primary};
    }
    .uwc-car__prev:disabled,
    .uwc-car__next:disabled {
      opacity: 0.4;
      pointer-events: none;
    }

    /* ── Indicators ─────────────────────────────────────────────────── */
    .uwc-car__indicators {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: ${t.space2};
      padding: ${t.space3} 0 ${t.space1};
    }
    :host([orientation="vertical"]) .uwc-car__indicators {
      flex-direction: column;
      padding: 0 ${t.space1} 0 ${t.space3};
      position: absolute;
      right: 0;
      top: 50%;
      transform: translateY(-50%);
    }

    .uwc-car__dot {
      width: 0.5rem;
      height: 0.5rem;
      border-radius: ${t.radiusFull};
      border: none;
      padding: 0;
      background: var(--uwc-carousel-dot-color, #cbd5e1);
      cursor: pointer;
      transition: background ${t.durationBase}, transform ${t.durationBase};
      flex-shrink: 0;
    }
    .uwc-car__dot:hover { background: var(--uwc-carousel-dot-hover, #94a3b8); }
    .uwc-car__dot.active {
      background: var(--uwc-carousel-dot-active, ${t.primary});
      transform: scale(1.35);
    }

    /* ── Transition ─────────────────────────────────────────────────── */
    .uwc-car__track.is-animating {
      transition: transform var(--uwc-carousel-duration, 400ms) ${t.easingStandard};
    }

    /* ── Reduced motion ─────────────────────────────────────────────── */
    @media (prefers-reduced-motion: reduce) {
      .uwc-car__track.is-animating { transition: none; }
      .uwc-car__prev,
      .uwc-car__next,
      .uwc-car__dot { transition: none; }
    }
  `,
];
